import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { fullWidth, halfWidth, thirdWidth } from "../../blocks/layout/columnSpans";

/** The organisation's communication options. Everything past activation waits for it. */
export const communicationSettingsPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "g4c_communicationoptions",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      // Its own full-width row, which drops out once activated.
      id: "row-activation",
      columns: [
        {
          id: "column-activation",
          span: fullWidth,
          blocks: [
            {
              kind: "section",
              id: "section-activation-required",
              title: "Activation",
              visibleWhen: "notActivated",
              fields: [
                {
                  id: "field-activation-notice",
                  type: "component",
                  name: "activationNotice",
                  label: "",
                  componentKey: "communicationActivationNotice",
                  span: fullWidth,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 12, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-sender",
              title: "Sender",
              helperText: "Who communications come from, and where they lead once expired.",
              visibleWhen: "isActivated",
              fields: [
                {
                  id: "field-g4c_fromname",
                  type: "text",
                  name: "g4c_fromname",
                  label: "From Name",
                  span: thirdWidth,
                },
                {
                  id: "field-g4c_fromaddress",
                  type: "email",
                  name: "g4c_fromaddress",
                  label: "From Address",
                  validatorKey: "communicationFromAddress",
                  span: thirdWidth,
                },
                {
                  id: "field-g4c_toname",
                  type: "text",
                  name: "g4c_toname",
                  label: "To Name",
                  span: thirdWidth,
                },
                {
                  id: "field-g4c_expirypage",
                  type: "text",
                  name: "g4c_expirypage",
                  label: "Expiry Page",
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-delivery",
              title: "Delivery",
              helperText: "How live communications are batched and processed.",
              visibleWhen: "isActivated",
              fields: [
                {
                  id: "field-g4c_batchsize",
                  type: "number",
                  name: "g4c_batchsize",
                  label: "Batch Size",
                  span: thirdWidth,
                },
                {
                  id: "field-g4c_defaultduration",
                  type: "number",
                  name: "g4c_defaultduration",
                  label: "Default Duration (days)",
                  span: thirdWidth,
                },
                {
                  id: "field-g4c_defaultrunat",
                  type: "picklist",
                  name: "g4c_defaultrunat",
                  label: "Default Run At",
                  required: true,
                  options: [
                    { Key: "crm", Value: "CRM" },
                    { Key: "warehouse", Value: "Warehouse" },
                  ],
                  span: thirdWidth,
                  visibleWhen: "warehouseAudienceEnabled",
                },
                {
                  id: "field-g4c_agentid",
                  type: "text",
                  name: "g4c_agentid",
                  label: "Agent Id",
                  span: thirdWidth,
                },
                {
                  id: "field-azurequeuename",
                  type: "text",
                  name: "azureQueueName",
                  label: "Azure Queue Name",
                  span: { desktop: 8, tablet: 6, mobile: 12 },
                },
              ],
            },
          ],
        },
        {
          id: "column-secondary",
          span: { desktop: 3, tablet: 12, mobile: 12 },
          // Activation, the marketing lists and the automation defaults are settings about
          // the account rather than about sending, so they share one card beside the sending details.
          card: true,
          fillHeight: true,
          blocks: [
            {
              kind: "section",
              id: "section-activation",
              title: "Activation",
              visibleWhen: "isActivated",
              fields: [
                {
                  id: "field-g4c_customerid",
                  type: "component",
                  name: "g4c_customerid",
                  label: "Customer Id",
                  componentKey: "communicationCustomerId",
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-marketing-lists",
              title: "Marketing Lists",
              visibleWhen: "isActivated",
              fields: [
                {
                  id: "field-g4c_includecontactmarketinglist",
                  type: "lookup",
                  name: "g4c_includecontactmarketinglist",
                  label: "Include in all communications",
                  lookupEntity: "list",
                  searchable: true,
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-automations",
              title: "Automations",
              visibleWhen: "isActivated",
              fields: [
                {
                  id: "field-g4ca_flowtestlistcontact",
                  type: "lookup",
                  name: "g4ca_flowtestlistcontact",
                  label: "Automation test list",
                  helpText:
                    "When automations are in Test mode, only contacts in this static marketing list will receive emails.",
                  lookupEntity: "list",
                  searchable: true,
                  span: fullWidth,
                },
                {
                  id: "field-g4ca_sendwindowstart",
                  type: "time",
                  name: "g4ca_sendwindowstart",
                  label: "Default send from",
                  helpText: "Triggered automations only. Earliest time of day emails send.",
                  validatorKey: "communicationSendWindowStart",
                  span: halfWidth,
                },
                {
                  id: "field-g4ca_sendwindowend",
                  type: "time",
                  name: "g4ca_sendwindowend",
                  label: "Default send until",
                  helpText: "Triggered automations only. Time of day emails stop sending.",
                  validatorKey: "communicationSendWindowEnd",
                  span: halfWidth,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
